
export enum TicketStatus {
  NEW = 'New',
  IN_PROGRESS = 'In Progress',
  WAITING_FOR_PARTS = 'Waiting for Parts',
  WAITING_FOR_CUSTOMER_APPROVAL = 'Waiting for Customer Approval', // New
  READY_FOR_PICKUP = 'Ready for Pickup',
  COMPLETED = 'Completed',
  CUSTOMER_DECLINED = 'Customer Declined', // New
}

export enum TicketPriority {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High',
}

// SLA Specific types
export type SLAPriority = TicketPriority; // SLAs are based on TicketPriority for now

export enum SLAStatus {
  OK = 'OK',
  AT_RISK = 'At Risk',
  BREACHED = 'Breached',
  COMPLETED_ON_TIME = 'Completed On Time',
  COMPLETED_LATE = 'Completed Late',
  NOT_APPLICABLE = 'N/A', // For statuses like Customer Declined, or if SLA doesn't apply
}

export interface SLARule {
  priority: SLAPriority;
  respondWithinHours: number; // Wall-clock hours
  resolveWithinHours: number; // Wall-clock hours
}

export enum YesNoUnknown {
  YES = 'Yes',
  NO = 'No',
  UNKNOWN = 'Unknown',
}

export enum PaymentStatus {
  UNPAID = 'Unpaid',
  PAID = 'Paid',
  PENDING = 'Pending',
  REFUNDED = 'Refunded',
}

export interface InternalTicketNote {
  id: string;
  text: string;
  createdAt: string;
  createdBy: string; // Employee name or ID
}

// New: For granular repair progress
export enum RepairStageStatus {
  PENDING = 'Pending',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  SKIPPED = 'Skipped', // If a stage is not applicable
}

export interface RepairStage {
  id: string; // e.g., 'diagnosis', 'parts_ordering'
  name: string; // User-friendly name, e.g., "Initial Diagnosis", "Ordering Parts"
  status: RepairStageStatus;
  startedAt?: string; // ISO timestamp
  completedAt?: string; // ISO timestamp
  notes?: string; // Optional internal notes for this stage, not visible to customer
}

export enum CommunicationType {
  SMS = 'SMS',
  EMAIL = 'Email',
  CALL = 'Call',
  SYSTEM = 'System Message', // For automated logs
  CUSTOMER_PORTAL = 'Customer Portal',
}

export interface CommunicationLogEntry {
  id: string;
  type: CommunicationType;
  timestamp: string;
  content: string;
  recipient?: string; // e.g., customer phone/email if different from primary
  staffName?: string; // Name of staff who logged or system action
}

// Checklist Item type
export interface ChecklistItem {
  id: string;
  text: string;
  isCompletedByDefault?: boolean;
  requiredRole?: EmployeeRole | EmployeeRole[]; // Optional: role(s) that should perform this
}

// Repair Checklist type
export interface RepairChecklist {
  id: string;
  title: string;
  description?: string;
  items: ChecklistItem[];
  associatedCategory?: string; // e.g., "Screen Repair", "iPhone General"
  applicableDeviceTypes?: string[]; // e.g., ["iPhone", "MacBook Pro"]
  createdAt: string;
  updatedAt: string;
}

export interface PartUsageEntry {
  id: string; // Unique ID for this usage entry
  inventoryItemId: string; // Link to InventoryItem.id
  itemName: string; // Denormalized for easy display
  quantityUsed: number;
  unitCostAtTimeOfUse?: number; // Cost of one unit when added
  unitPriceAtTimeOfUse?: number; // Price of one unit when added
  addedAt: string; // ISO timestamp
  addedBy: string; // Employee name or ID
}

export interface Ticket {
  id: string;
  subject: string; // Corresponds to "Issue with device"
  description: string; // Corresponds to "Additional Details About Repair"
  status: TicketStatus;
  priority: TicketPriority;
  customer: string; // Customer's name, links to Customer profile
  assignedTo?: string; // Employee's name
  createdAt: string;
  updatedAt: string;

  // New fields from Service Request Sheet
  customerPhoneNumber?: string;
  customerEmail?: string;
  deviceModel?: string;
  deviceSerialNumber?: string;
  devicePasscode?: string; // Store with caution, ensure policy compliance
  dataBackupStatus?: YesNoUnknown;
  findMyDeviceDisabled?: YesNoUnknown;
  referralSource?: string; // How did you hear about us?

  // Payment fields
  amountDue: number;
  paymentStatus: PaymentStatus;

  // Estimate fields
  estimateDetails?: string; // New: For customer portal estimate display
  isEstimateApproved?: boolean; // New: Tracks customer approval

  // Internal Notes
  internalNotes: InternalTicketNote[];

  // Granular Repair Progress
  repairProgress: RepairStage[];

  // Communication Log
  communicationLog: CommunicationLogEntry[];

  // AI Suggested fields
  category?: string; // User-confirmed or technician-set category
  aiSuggestedCategory?: string;
  aiSuggestedPriority?: TicketPriority;

  // SLA Calculated Fields (to be populated dynamically)
  respondByDate?: string;
  resolveByDate?: string;
  responseSlaStatus?: SLAStatus;
  resolutionSlaStatus?: SLAStatus;

  // Checklist progress for this specific ticket
  activeChecklistId?: string; // ID of the checklist currently applied to this ticket
  checklistProgress?: { [checklistItemId: string]: boolean }; // Maps item ID to completion status

  // Parts Used for Repair
  partsUsed: PartUsageEntry[];
}

export type FilterType = TicketStatus | 'ALL';

export interface CustomerNote {
  text: string;
  date: string;
}

export interface Customer {
  id: string;
  name: string;
  phoneNumber?: string;
  email?: string;
  // address?: string; // Future enhancement
  createdAt: string;
  notes: CustomerNote[];
}

// Define standard employee roles for clarity
export type EmployeeRole = 'Technician' | 'Senior Technician' | 'Lead Technician' | 'Manager' | 'Administrator' | 'Inactive';


export interface Employee {
  id:string;
  name: string;
  role: EmployeeRole | string; // Allow current string roles but prefer EmployeeRole
  email?: string;
  phone?: string;
  status: 'Active' | 'Inactive'; // Added status
  lastLogin?: string; // Added optional lastLogin
}

export interface InventoryItem {
  id: string;
  name: string;
  sku?: string;
  category?: string;
  stock: number;
  price?: number;       // Sale price
  cost?: number;        // Purchase cost (for 'service' source)
  supplier?: string;
  lastSyncedAt?: string; // Timestamp of last sync (for 'square' source)
  source: 'service' | 'square';
  notes?: string;
  usageType?: 'general' | 'repair' | 'sale'; // New field for service items
}

export interface DashboardCardVisibilityConfig {
  activeRepairs: boolean;
  revenueSummary: boolean;
  newRepairsToday: boolean;
  newTickets: boolean;
  inProgressTickets: boolean;
  waitingCustomerApprovalTickets: boolean;
  waitingForPartsTickets: boolean;
  readyForPickupTickets: boolean;
  quickAppraisalWidget?: boolean;
  repairTypeBreakdown?: boolean;
  newCheckInsPanel?: boolean;
}

export interface AppSettings {
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyWebsite: string;
  defaultTicketPriority: TicketPriority;
  intakeAgreementText: string;
  completionAgreementText: string;
  logoBase64: string | null;
  primaryColor: string;
  accentColor: string;
  dashboardCardVisibility: DashboardCardVisibilityConfig;
}

// --- Buyback Widget Types ---
export enum BuybackDeviceType {
  PHONE = 'iPhone',
  TABLET = 'iPad',
  LAPTOP = 'MacBook',
  SMARTWATCH = 'Apple Watch',
}

export interface DeviceModelInfo {
  id: string;
  name: string;
  baseValue: number;
  storageOptions?: string[];
}

export enum BuybackCondition {
  LIKE_NEW = 'Like New',
  GOOD = 'Good',
  FAIR = 'Fair',
  POOR_BROKEN = 'Poor / Broken',
}

export enum BuybackAccessory {
  ORIGINAL_BOX = 'Original Box',
  CHARGER = 'Charger',
  CABLE = 'Cable',
}

export interface BuybackRequest {
  id: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;

  deviceType: BuybackDeviceType;
  deviceModel: string;
  storageCapacity?: string;
  imeiOrSerial?: string;

  overallCondition: BuybackCondition;
  powersOn: boolean;
  screenIntact: boolean;
  noMajorScratchesDents: boolean;
  buttonsPortsFunctional: boolean;
  noWaterDamage: boolean;
  notAccountLocked: boolean;

  includedAccessories: BuybackAccessory[];
  additionalNotes?: string;

  estimatedValue: number;
  finalOfferedValue?: number; // Value agreed upon after inspection
  status: 'Pending Review' | 'Offer Made' | 'Completed' | 'Declined';
  createdAt: string;
  currentInventoryDeviceId?: string; // Link to TradedInDevice.id if processed
}

export interface TradedInDevice {
  id: string; // Unique ID for this inventory entry
  buybackRequestId: string; // ID of the original BuybackRequest
  originalCustomerName: string;
  originalCustomerEmail?: string;
  deviceType: BuybackDeviceType;
  deviceModelName: string; // Name of the model, e.g., "iPhone 13 Pro"
  imeiOrSerial?: string;
  storageCapacity?: string;
  overallConditionAtTradeIn: BuybackCondition;
  finalTradeInValue: number;
  dateAddedToInventory: string; // ISO timestamp
  currentStatus: 'In Stock' | 'For Refurbishment' | 'Sold' | 'Recycled'; // Status within your inventory
  notes?: string; // Admin notes for this inventory item
}


export interface ConditionQuestion {
  id: keyof Pick<BuybackRequest, 'powersOn' | 'screenIntact' | 'noMajorScratchesDents' | 'buttonsPortsFunctional' | 'noWaterDamage' | 'notAccountLocked'>;
  label: string;
  positiveImpact: boolean;
  valueAdjustmentFactor: number;
}

export interface BuybackPricingFactors {
  conditionMultiplier: Record<BuybackCondition, number>;
  storageBonusPercentage: Record<string, number>;
  accessoryValue: Record<BuybackAccessory, number>;
  MINIMUM_POOR_VALUE: number;
}


// Label Printing
export interface LabelSize {
  id: string; // e.g., 'dymo_30252_address'
  name: string; // User-friendly name, e.g., "Dymo 30252 Address (1-1/8\" x 3-1/2\")"
  widthInches: number;
  heightInches: number;
  printClassName: string; // CSS class for @media print specific styles
}

// Dashboard Ticket Volume Trend
export interface TicketTrendData {
  currentPeriodCount: number;
  previousPeriodCount: number;
  percentageChange: number;
  trendDirection: 'up' | 'down' | 'steady';
}

// Special Filter for AllTicketsPage
export type SpecialTicketFilterCriteria = 'unpaid' | null;

// Dashboard Repair Type Breakdown
export interface RepairBreakdownSegment {
  type: string;
  count: number;
  percentage: number;
  color: string;
}

// Application Activity Log
export enum ActivityLogTargetType {
  TICKET = 'Ticket',
  CUSTOMER = 'Customer',
  SYSTEM = 'System',
  SETTINGS = 'Settings',
  INVENTORY = 'Inventory',
  USER = 'User',
  BUYBACK = 'Buyback',
  PAYMENT = 'Payment',
  APPOINTMENT = 'Appointment', // New
}

export interface ActivityLogEntry {
  id: string;
  timestamp: string; // ISO date string
  user: string; // Name of the user or 'System'
  action: string; // Description of the action, e.g., "Created Ticket", "Updated Status"
  targetType?: ActivityLogTargetType; // Optional: Type of entity acted upon
  targetId?: string; // Optional: ID of the entity (e.g., ticket ID, customer ID)
  details?: string; // Optional: Additional context or data for the log
}

// Reports Page Types
export interface TechnicianPerformanceData {
  technicianName: string;
  totalAssigned: number;
  ticketsClosed: number;
  avgResolutionTimeMs?: number; // Store in ms for easier calculations, format for display
}

export type DateRangePreset = 'today' | 'yesterday' | 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth' | 'allTime';

export interface DateRange {
  startDate: Date;
  endDate: Date;
  preset: DateRangePreset;
}

export interface TicketsByStatusData {
  status: TicketStatus;
  count: number;
  colorClass: string; // For visual representation, e.g., 'bg-sky-500'
}

export interface TicketsByCategoryData {
  category: string;
  count: number;
}

export interface TicketTrendDataItem {
  period: string; // e.g., '2024-07-29', 'Week 30', 'July 2024'
  count: number;
}

export interface RevenueReportData {
  totalRevenueInPeriod: number;
  totalOutstandingAmount: number; // Could be all-time or period-specific
  paidTicketsInPeriod: Ticket[];
  outstandingTicketsList: Ticket[]; // List of tickets that are unpaid
}

// Appointment Scheduling Types
export interface Appointment {
  id: string;
  ticketId?: string; // Optional link to a ticket
  customerName: string;
  serviceDescription: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:MM (24-hour format for easier sorting/comparison)
  endTime: string; // HH:MM
  durationMinutes: number;
  assignedTechnicianId?: string; // Employee ID
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AppointmentModalData {
  appointment?: Appointment;
  ticketId?: string;
  customerName?: string;
  date?: string; // YYYY-MM-DD
}

// Pre-Completion Check Data
export interface PreCompletionCheckData {
  ticket: Ticket;
  targetStatus: TicketStatus.COMPLETED | TicketStatus.READY_FOR_PICKUP;
  targetSendSms?: boolean;
  targetSendCompletionEmail?: boolean;
}
